import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { CONFIGURABLE_COLORS } from "@miplatform/constants";
import { ITrayConfig } from "@miplatform/types";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";

export const CalendarTrayCard = ({
  tray,
  isReadOnly,
  isFirstInList,
  isLastInList,
  onDeleteClick,
  onEditClick,
  onReorderUp,
  onReorderDown,
}: Readonly<CalendarTrayCardProps>) => {
  const { classes, theme } = useStyles();
  const currentColor = useMemo(() => {
    return CONFIGURABLE_COLORS.find(
      (color) => color.color === tray.headerBackgroundColor
    );
  }, [tray.headerBackgroundColor]);

  return (
    <Box className={classes.configListCardContent}>
      <Box className={classes.configListCard}>
        <Box className={classes.configListCardContent}>
          <Box className={classes.configListCardText}>
            <Box className={classes.configListCardTextLine}>
              <Typography variant="HD2" color={theme.palette.primary.main}>
                {tray.name}
              </Typography>
              <Divider orientation="vertical" />
              <Typography variant="BD2" color={currentColor?.color}>
                {currentColor?.label}
              </Typography>
            </Box>
          </Box>
        </Box>
        {!isReadOnly && (
          <Box className={classes.configListCardActions}>
            {!tray.isDefault && (
              <MiIconButton onClick={() => onDeleteClick(tray.id)}>
                <MiIcon
                  path={mdiTrashCanOutline}
                  color={theme.palette.red.main}
                />
              </MiIconButton>
            )}
            <MiIconButton onClick={() => onEditClick(tray.id)}>
              <MiIcon
                path={mdiPencilOutline}
                color={theme.palette.primary.main}
              />
            </MiIconButton>
          </Box>
        )}
      </Box>
      {!isReadOnly && (
        <Box className={classes.arrowsContainer}>
          <MiIcon
            path={mdiChevronUp}
            color={theme.palette.primary.main}
            onClick={() => onReorderUp(tray.id)}
            disabled={isFirstInList}
            size={0.85}
          />
          <MiIcon
            path={mdiChevronDown}
            color={theme.palette.primary.main}
            onClick={() => onReorderDown(tray.id)}
            disabled={isLastInList}
            size={0.85}
          />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  configListCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    flex: 1,
  },
  configListCardContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  configListCardActions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  configListCardText: {
    display: "flex",
    flexDirection: "column",
  },
  configListCardTextLine: {
    height: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  arrowsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export interface CalendarTrayCardProps {
  tray: ITrayConfig;
  isReadOnly?: boolean;
  isFirstInList?: boolean;
  isLastInList?: boolean;
  onDeleteClick: (trayId: string) => void;
  onEditClick: (trayId: string) => void;
  onReorderUp: (trayId: string) => void;
  onReorderDown: (trayId: string) => void;
}
